import { FileImageIcon } from 'lucide-react';

import type { ItemAvailability } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import {
  type ExpenseLineItem,
  type LineItemsRest,
  formatAmountForCurrency,
} from '@splitfiesta/shared';
import { Button, Card } from '@splitfiesta/ui';

import { LineItemRow } from './LineItemRow';
import { RestRow } from './RestRow';

/** `editable` is the only state in which the viewer can open the claim; `notParticipant` sees the summary alone. */
export type LineItemsClaimState = 'editable' | 'offline' | 'notParticipant';

export interface LineItemsSectionProps {
  rows: Array<{
    item: ExpenseLineItem;
    /** For the viewer; `takenBy` in the order the names should read. */
    availability: ItemAvailability;
    unpickedHint?: boolean;
  }>;
  nameOf: (userId: string) => string;
  /** The entry currency, the one printed on the receipt. */
  currency: string;
  rest: {
    kind: 'rest' | 'restNegative';
    mode: LineItemsRest['mode'];
    amount: number;
  } | null;
  state: LineItemsClaimState;
  /** The units the viewer holds as saved. */
  pickedUnits: number;
  /** Omitted once the table was asked: the picks card is then the one place to pick. */
  onPick?: () => void;
  /** Shown only when a receipt exists. */
  onViewReceipt?: () => void;
}

/** The detail's read-only summary of who has what; picking happens in the claim dialog. */
export function LineItemsSection({
  rows,
  nameOf,
  currency,
  rest,
  state,
  pickedUnits,
  onPick,
  onViewReceipt,
}: LineItemsSectionProps) {
  const { t } = useTranslation('expenses');
  const participant = state !== 'notParticipant';
  const canPick = !!onPick && (state === 'editable' || state === 'offline');

  return (
    <section
      data-testid="line-items-section"
      aria-labelledby="line-items-title"
    >
      <Card className="p-6 rounded-lg">
        <h3
          id="line-items-title"
          className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100"
        >
          {t('lineItems.sectionTitle')}
        </h3>
        {onViewReceipt ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onViewReceipt}
            leftIcon={<FileImageIcon aria-hidden="true" className="h-4 w-4" />}
            className="min-h-11"
          >
            {t('scan.viewReceipt')}
          </Button>
        ) : null}

        <ul className="mt-3">
          {rows.map(({ item, availability, unpickedHint }) => (
            <LineItemRow
              key={item.id}
              item={item}
              availability={availability}
              nameOf={nameOf}
              currency={currency}
              unpickedHint={unpickedHint}
              readOnly
              onToggle={() => undefined}
              onUnits={() => undefined}
            />
          ))}
        </ul>

        {rest ? (
          <RestRow
            testId="line-items-rest"
            label={t(
              rest.kind === 'restNegative'
                ? 'lineItems.restNegative'
                : 'lineItems.rest'
            )}
            amount={formatAmountForCurrency(rest.amount, currency)}
            mode={rest.mode}
          />
        ) : null}

        {participant ? (
          <div
            data-testid="line-items-status"
            className="mt-4 space-y-2 border-t border-gray-200 pt-4 dark:border-gray-700"
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <p className="min-w-[45%] flex-1 text-base text-gray-900 dark:text-gray-100">
                {pickedUnits > 0
                  ? t('lineItems.youPicked', { count: pickedUnits })
                  : t('lineItems.youPickedNone')}
              </p>
              {canPick ? (
                <Button
                  type="button"
                  variant="primary"
                  onClick={onPick}
                  disabled={state !== 'editable'}
                  className="ml-auto min-h-11"
                >
                  {t(
                    pickedUnits > 0
                      ? 'lineItems.changeMyItems'
                      : 'lineItems.pickMyItems'
                  )}
                </Button>
              ) : null}
            </div>
            {state === 'offline' ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('lineItems.claimOffline')}
              </p>
            ) : null}
          </div>
        ) : null}
      </Card>
    </section>
  );
}

export default LineItemsSection;
